import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateLandingAudienceBlockInput,
    DeleteLandingAudienceBlockInput,
    LandingAudienceBlock,
    ModelLandingAudienceBlockFilterInput,
    UpdateLandingAudienceBlockInput
} from '@app/core/services/api/API';

@Injectable({
    providedIn: 'root'
})
export class LandingAudienceBlockService {
    private amplifyClient = generateClient();
    private _cachedBlocks: Map<string, LandingAudienceBlock[]> = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<
        string,
        Promise<LandingAudienceBlock[]>
    > = new Map();

    private _getCacheKey(filter: ModelLandingAudienceBlockFilterInput): string {
        return JSON.stringify(filter || {});
    }

    async listLandingAudienceBlocks(
        filter: ModelLandingAudienceBlockFilterInput = null
    ): Promise<LandingAudienceBlock[]> {
        const cacheKey = this._getCacheKey(filter);

        if (this._cachedBlocks.has(cacheKey)) {
            return this._cachedBlocks.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchBlocks(filter, cacheKey);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchBlocks(
        filter: ModelLandingAudienceBlockFilterInput,
        cacheKey: string
    ): Promise<LandingAudienceBlock[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listLandingAudienceBlocks,
                    variables: { filter }
                })
            ).data.listLandingAudienceBlocks.items;

            if (data) {
                const blocks = data as LandingAudienceBlock[];
                this._cachedBlocks.set(cacheKey, blocks);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return blocks;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        } catch (error) {
            console.error(
                '[LandingAudienceBlock] listLandingAudienceBlocks failed:',
                error
            );
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        }
    }

    clearCache(): void {
        this._cachedBlocks.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getLandingAudienceBlock(id: string): Promise<LandingAudienceBlock> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getLandingAudienceBlock,
                    variables: { id }
                })
            ).data.getLandingAudienceBlock;
            if (data) {
                return data as LandingAudienceBlock;
            }
        } catch (error) {
            console.error('Error finding LandingAudienceBlock:', error);
        }
        return null;
    }

    async createLandingAudienceBlock(
        input: CreateLandingAudienceBlockInput
    ): Promise<LandingAudienceBlock> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createLandingAudienceBlock,
                    variables: { input }
                })
            ).data.createLandingAudienceBlock;
            if (data) {
                this.clearCache();
                return data as LandingAudienceBlock;
            }
        } catch (error) {
            console.error('Error creating LandingAudienceBlock:', error);
        }
        return null;
    }

    async updateLandingAudienceBlock(
        input: UpdateLandingAudienceBlockInput
    ): Promise<LandingAudienceBlock> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updateLandingAudienceBlock,
                    variables: { input }
                })
            ).data.updateLandingAudienceBlock;
            if (data) {
                this.clearCache();
                return data as LandingAudienceBlock;
            }
        } catch (error) {
            console.error('Error updating LandingAudienceBlock:', error);
        }
        return null;
    }

    async deleteLandingAudienceBlock(
        input: DeleteLandingAudienceBlockInput
    ): Promise<LandingAudienceBlock> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteLandingAudienceBlock,
                    variables: { input }
                })
            ).data.deleteLandingAudienceBlock;
            if (data) {
                this.clearCache();
                return data as LandingAudienceBlock;
            }
        } catch (error) {
            console.error('Error deleting LandingAudienceBlock:', error);
        }
        return null;
    }
}
